import { FC, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { classes, stylesheet } from 'typestyle'

/* Hooks ======================================================================================== */
import useFetch from '../../__aeki__/hooks/use-fetch'
import useBoolean from '../../__aeki__/hooks/use-boolean'

/* Utils ======================================================================================== */
import { get } from '../../__aeki__/share/find'

/* Components =================================================================================== */
import AppLayout, { MAX_WIDTH } from '../blog-post/layout'
import Footer from '../__home__/components/footer'
import { Box } from '../../__aeki__/components/box/box.component'
import { Heading } from '../../__aeki__/components/text/heading.component'
import { Info } from './components/info'
import { Line } from '../../__aeki__/components/line'
import { RichTextView } from '../../__aeki__/components/field-editor-field/view/richt-text-view'
import { Text } from '../../__aeki__/components/text/text.component'
import { Title } from '../../__aeki__/components/text/title.component'
import { VisibleFade } from '../__home__/components/visible/visible-fade'
import { VisibleOpacity } from '../__home__/components/visible/visible-opacity'

/* Styles ======================================================================================= */
import { mb12 } from '../../__aeki__/styles/styleset/margin/mb12'
import { mb24 } from '../../__aeki__/styles/styleset/margin/mb24'
import { mb8 } from '../../__aeki__/styles/styleset/margin/mb8'
import { paddingVerticalOuter } from '../../__aeki__/styles/default/padding'
import { pt40 } from '../../__aeki__/styles/styleset/padding/pt40'
import { rowsmi } from '../../__aeki__/styles/styleset/row/rowsmi'
import { createMediaQuery } from '../../__aeki__/components/style-class/style-class.utils'
import { textCenter } from '../../__aeki__/styles/styleset/text-center/textCenter'
import { mt40 } from '../../__aeki__/styles/styleset/margin/mt40'

/* <Posts /> ==================================================================================== */
const Posts: FC<any> = (props: any) => {
  const { inject, eject } = props
  const injection = inject()
  const historyLocation = useLocation()
  const url = `${process.env.REACT_APP_API_URL}${historyLocation.pathname}`
  const [, $isNotFound] = useBoolean(false)

  const [post, $post] = useFetch(
    Object.keys(injection).length
      ? injection
      : {
          body: {
            allowBookmarks: false,
            body: [],
          },
        },
  )

  useEffect(() => {
    return () => {
      eject()
    }
  }, [])

  useEffect(() => {
    if (post.mounted && Object.keys(injection).length === 0) {
      $post.get({ url }).catch(() => {
        $isNotFound.set(true)
      })
    }
  }, [post.mounted])

  const title = get('body.title', post.payload, 'string', '')
  const summary = get('body.summary', post.payload, 'string')

  function renderDate() {
    const updatedAt = get('createdAt', post.payload, 'string', '')
    const createdAt = get('createdAt', post.payload, 'string', '')

    if (updatedAt) {
      return (
        <Text size="sm" baseline="surface">
          Updated at{' '}
          {new Date(updatedAt).toLocaleDateString(undefined, {
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            hour12: true,
          })}
        </Text>
      )
    }

    if (createdAt) {
      return (
        <Text size="sm" baseline="surface">
          Created at{' '}
          {new Date(createdAt).toLocaleDateString(undefined, {
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            hour12: true,
          })}
        </Text>
      )
    }
  }

  const content = useMemo(() => {
    const body = get('body.body', post.payload, 'array')
    const content = get('body.content', post.payload, 'string', null)

    return (
      <Box baseline="surface">
        <Box baseline="secondary" className={STYLES.containerHero} row>
          <Line className={STYLES.lineHero} />
          <Box
            baseline="none"
            className={STYLES.containerHeroContent}
            flex
            alignCenterX
            alignCenterY
          >
            <Box
              className={classes(paddingVerticalOuter)}
              baseline="none"
              maxWidth={MAX_WIDTH}
              centerAutoX
              fullWidth
              row
            >
              <VisibleOpacity>
                <Box className={rowsmi} row baseline="none" alignCenterX>
                  <Title className={classes(mb8, textCenter)} size="md" tag="h1" semiBold crank>
                    {title}
                  </Title>
                </Box>
              </VisibleOpacity>
            </Box>
          </Box>
        </Box>
        <Info maxWidth={MAX_WIDTH} label="Info">
          {renderDate()}
        </Info>
        <Box baseline="surface" row flex>
          <VisibleFade>
            <Box
              className={classes(paddingVerticalOuter, pt40)}
              baseline="none"
              maxWidth={MAX_WIDTH}
              centerAutoX
              fullWidth
              row
            >
              <Box baseline="none" className={classes(rowsmi)} column>
                {content ? (
                  <Box baseline="none" maxWidth={MAX_WIDTH} row>
                    <RichTextView __html={content} scroll={false} disablePadding />
                  </Box>
                ) : (
                  ''
                )}
              </Box>
              <Box baseline="none" maxWidth={MAX_WIDTH} row>
                {body.map((section: any, key: any) => {
                  return (
                    <Box
                      className={classes(key !== body.length - 1 && mb24)}
                      baseline="none"
                      key={key}
                      relative
                      row
                    >
                      {section.subtitle ? (
                        <Heading className={mb12} size="xs">
                          {section.subtitle}
                        </Heading>
                      ) : (
                        ''
                      )}
                      <RichTextView __html={section.content} scroll={false} disablePadding />
                    </Box>
                  )
                })}
              </Box>
            </Box>
          </VisibleFade>
          <Line className={mt40} />
          <Box className={classes(pt40)} baseline="background">
            <VisibleOpacity>
              <Footer />
            </VisibleOpacity>
          </Box>
        </Box>
      </Box>
    )
  }, [post?.payload?.body?.content, post?.payload?.body?.body?.length > 0])

  return (
    <AppLayout.render
      head={{
        title: `${title} - ChildAt.io`,
        description: summary,
      }}
      headerProps={{
        maxWidth: MAX_WIDTH,
        scrollEffect: true,
        allowToggleEffect: true,
      }}
      content={content}
    />
  )
}

const STYLES = stylesheet({
  containerHero: {
    marginTop: -56,
    paddingTop: 56,
  },
  containerHeroContent: {
    minHeight: 580,
    paddingTop: 162,
    paddingBottom: 160,
    ...createMediaQuery.sm({
      minHeight: 'calc(100vh - 94px)',
    }),
  },
  lineHero: {
    opacity: 0.1,
  },
})

export default Posts
